import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, Cloud, Code, Target, AlertCircle, CheckCircle2, Zap, BarChart3, Database } from 'lucide-react';
interface Persona {
  id: string;
  name: string;
  role: string;
  icon: any;
  color: string;
  bgColor: string;
  iconColor: string;
  description: string;
  goals: string[];
  painPoints: string[];
  needs: string[];
  behaviors: string[];
}
const personas: Persona[] = [{
  id: 'trader',
  name: 'Energy Trader',
  role: 'Trading Desk Professional',
  icon: TrendingUp,
  color: 'from-pink-500 to-purple-500',
  bgColor: 'bg-pink-50',
  iconColor: 'text-pink-600',
  description: 'Focuses on positions, spreads, risk management, and intraday changes. Needs fast, decision-relevant answers to capitalize on market opportunities.',
  goals: ['Make profitable trading decisions quickly', 'Manage risk across multiple positions', 'Identify arbitrage opportunities', 'React to market changes in real-time'],
  painPoints: ['Too much data, not enough actionable insights', 'Slow access to critical weather forecasts', 'Difficulty comparing multiple forecast models', 'Missing context for risk assessment'],
  needs: ['Fast, decision-relevant answers', 'Clear visualization of spreads and positions', 'Real-time alerts for significant changes', 'Historical context for current conditions'],
  behaviors: ['Checks dashboard multiple times per day', 'Focuses on specific regions and timeframes', 'Compares forecasts across models', 'Uses mobile for quick checks']
}, {
  id: 'meteorologist',
  name: 'Meteorologist / Analyst',
  role: 'Weather & Forecast Specialist',
  icon: Cloud,
  color: 'from-cyan-500 to-blue-500',
  bgColor: 'bg-cyan-50',
  iconColor: 'text-cyan-600',
  description: 'Focuses on forecast quality, uncertainty quantification, and model differences. Wants to explore data deeply and validate insights.',
  goals: ['Validate forecast accuracy and reliability', 'Understand model differences and biases', 'Quantify uncertainty in predictions', 'Identify patterns and anomalies'],
  painPoints: ['Limited access to ensemble data', 'Difficulty comparing model performance', 'Lack of historical validation metrics', 'No way to track forecast skill over time'],
  needs: ['Detailed model comparison tools', 'Uncertainty visualization (confidence intervals)', 'Historical performance metrics', 'Access to raw forecast data'],
  behaviors: ['Deep dives into specific weather events', 'Analyzes model agreement and divergence', 'Reviews historical forecast performance', 'Explores multiple variables simultaneously']
}, {
  id: 'quant',
  name: 'Quant / Developer',
  role: 'Quantitative Analyst & Engineer',
  icon: Code,
  color: 'from-emerald-500 to-green-500',
  bgColor: 'bg-emerald-50',
  iconColor: 'text-emerald-600',
  description: 'Focuses on data sources, structure, reliability, and integration into automated workflows. Often uses APIs alongside the UI.',
  goals: ['Integrate forecast data into trading algorithms', 'Build automated decision systems', 'Backtest strategies with historical data', 'Monitor data quality and reliability'],
  painPoints: ['Inconsistent data formats', 'API rate limits and latency', 'Missing documentation for edge cases', 'Difficulty accessing historical data'],
  needs: ['Well-documented APIs with examples', 'Consistent data structure and schemas', 'Bulk data access for backtesting', 'Real-time data feeds with low latency'],
  behaviors: ['Uses API more than UI', 'Builds custom dashboards and tools', 'Runs automated tests and validations', 'Monitors system uptime and data quality']
}];
export function UserPersonas() {
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50" data-id="element-3742">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-3743">
        {/* Header */}
        <div className="mb-12" data-id="element-3744">
          <Link to="/ux" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors mb-6" data-id="element-3745">
            <ArrowLeft size={16} data-id="element-3746" />
            Back to UX Documentation
          </Link>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-3747">
            <h1 className="text-4xl font-bold text-slate-900 mb-4" data-id="element-3748">
              User Personas
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl" data-id="element-3749">
              Three primary user types drive our product decisions. Each has
              distinct goals, pain points, and interaction patterns with weather
              forecast data.
            </p>
          </motion.div>
        </div>

        {/* Personas */}
        <div className="space-y-8" data-id="element-3750">
          {personas.map((persona, index) => <motion.div key={persona.id} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.15
        }} className="bg-white/60 backdrop-blur-xl border border-slate-200/50 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow" data-id="element-3751">
              {/* Header */}
              <div className={`bg-gradient-to-r ${persona.color} p-6`} data-id="element-3752">
                <div className="flex items-start gap-4" data-id="element-3753">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl" data-id="element-3754">
                    <persona.icon size={32} className="text-white" data-id="element-3755" />
                  </div>
                  <div className="flex-1" data-id="element-3756">
                    <h2 className="text-2xl font-bold text-white mb-1" data-id="element-3757">
                      {persona.name}
                    </h2>
                    <p className="text-white/90 text-sm font-medium" data-id="element-3758">
                      {persona.role}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8" data-id="element-3759">
                <p className="text-slate-700 leading-relaxed mb-8" data-id="element-3760">
                  {persona.description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3761">
                  {/* Goals */}
                  <div data-id="element-3762">
                    <div className="flex items-center gap-2 mb-4" data-id="element-3763">
                      <div className={`p-2 ${persona.bgColor} rounded-lg`} data-id="element-3764">
                        <Target size={18} className={persona.iconColor} data-id="element-3765" />
                      </div>
                      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide" data-id="element-3766">
                        Goals
                      </h3>
                    </div>
                    <ul className="space-y-2" data-id="element-3767">
                      {persona.goals.map((goal, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-3768">
                          <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" data-id="element-3769" />
                          <span data-id="element-3770">{goal}</span>
                        </li>)}
                    </ul>
                  </div>

                  {/* Pain Points */}
                  <div data-id="element-3771">
                    <div className="flex items-center gap-2 mb-4" data-id="element-3772">
                      <div className="p-2 bg-red-50 rounded-lg" data-id="element-3773">
                        <AlertCircle size={18} className="text-red-600" data-id="element-3774" />
                      </div>
                      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide" data-id="element-3775">
                        Pain Points
                      </h3>
                    </div>
                    <ul className="space-y-2" data-id="element-3776">
                      {persona.painPoints.map((pain, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-3777">
                          <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" data-id="element-3778" />
                          <span data-id="element-3779">{pain}</span>
                        </li>)}
                    </ul>
                  </div>

                  {/* Needs */}
                  <div data-id="element-3780">
                    <div className="flex items-center gap-2 mb-4" data-id="element-3781">
                      <div className="p-2 bg-blue-50 rounded-lg" data-id="element-3782">
                        <Zap size={18} className="text-blue-600" data-id="element-3783" />
                      </div>
                      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide" data-id="element-3784">
                        Needs
                      </h3>
                    </div>
                    <ul className="space-y-2" data-id="element-3785">
                      {persona.needs.map((need, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-3786">
                          <Zap size={16} className="text-blue-600 mt-0.5 flex-shrink-0" data-id="element-3787" />
                          <span data-id="element-3788">{need}</span>
                        </li>)}
                    </ul>
                  </div>

                  {/* Behaviors */}
                  <div data-id="element-3789">
                    <div className="flex items-center gap-2 mb-4" data-id="element-3790">
                      <div className="p-2 bg-purple-50 rounded-lg" data-id="element-3791">
                        <BarChart3 size={18} className="text-purple-600" data-id="element-3792" />
                      </div>
                      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide" data-id="element-3793">
                        Behaviors
                      </h3>
                    </div>
                    <ul className="space-y-2" data-id="element-3794">
                      {persona.behaviors.map((behavior, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-3795">
                          <BarChart3 size={16} className="text-purple-600 mt-0.5 flex-shrink-0" data-id="element-3796" />
                          <span data-id="element-3797">{behavior}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Summary Card */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="mt-12 p-6 bg-gradient-to-br from-slate-50 to-slate-100/50 backdrop-blur-xl border border-slate-200/50 rounded-xl" data-id="element-3798">
          <div className="flex items-start gap-3" data-id="element-3799">
            <div className="p-2 bg-slate-200 rounded-lg" data-id="element-3800">
              <Database size={20} className="text-slate-700" data-id="element-3801" />
            </div>
            <div data-id="element-3802">
              <h3 className="text-sm font-semibold text-slate-900 mb-2" data-id="element-3803">
                Design Implications
              </h3>
              <p className="text-sm text-slate-700 leading-relaxed mb-3" data-id="element-3804">
                These personas inform every product decision:
              </p>
              <ul className="space-y-2 text-sm text-slate-700" data-id="element-3805">
                <li className="flex items-start gap-2" data-id="element-3806">
                  <span className="text-pink-600 font-bold" data-id="element-3807">•</span>
                  <span data-id="element-3808">
                    <strong data-id="element-3809">Traders</strong> need speed and clarity → Dashboard
                    prioritizes key metrics and fast load times
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-3810">
                  <span className="text-cyan-600 font-bold" data-id="element-3811">•</span>
                  <span data-id="element-3812">
                    <strong data-id="element-3813">Meteorologists</strong> need depth → Advanced views
                    with model comparisons and uncertainty bands
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-3814">
                  <span className="text-emerald-600 font-bold" data-id="element-3815">•</span>
                  <span data-id="element-3816">
                    <strong data-id="element-3817">Quants</strong> need integration → Robust APIs with
                    comprehensive documentation
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}